"use client"

import React from "react"
import { useEffect, useState, useRef } from "react"
import { createPortal } from "react-dom"
import dynamic from "next/dynamic"

const DynamicLandingSignupModal = dynamic(() => import("@/components/landing/LandingSignupModal"), { ssr: false })

function isSearchEngine(userAgent: string): boolean {
    const searchEngines = ["Googlebot", "Bingbot", "Slurp", "DuckDuckBot", "Baiduspider", "YandexBot", "Sogou", "Exabot", "facebookexternalhit", "ia_archiver"]
    return searchEngines.some((bot) => userAgent.toLowerCase().includes(bot.toLowerCase()))
}

export default function DelayedModalLoader() {
    const [showModal, setShowModal] = useState(false)
    const [isRealUser, setIsRealUser] = useState(false)
    const [isMounted, setIsMounted] = useState(false)
    const [conversionStatus, setConversionStatus] = useState<"new" | "returning">("new")
    const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null)
    const timeTimerRef = useRef<NodeJS.Timeout | null>(null)

    // Track client-side mount for portal
    useEffect(() => {
        setIsMounted(true)
    }, [])

    useEffect(() => {
        if (typeof window !== "undefined") {
            const userAgent = window.navigator.userAgent
            setIsRealUser(!isSearchEngine(userAgent))
            localStorage.setItem("visited_before", "true")
        }
    }, [])

    useEffect(() => {
        setConversionStatus(localStorage.getItem("visited_before") ? "returning" : "new")
    }, [])

    useEffect(() => {
        if (typeof window !== "undefined") {
            // Progressive cooldowns: show up to 3 times with increasing delays
            // Show 1: immediate, Show 2: 15min wait, Show 3: 45min wait, then 4hr reset
            const COOLDOWNS = [0, 900000, 2700000, 14400000] // 0, 15min, 45min, 4hr
            
            const showCount = parseInt(localStorage.getItem("modal_show_count") || "0")
            const lastClose = localStorage.getItem("last_modal_close")
            const cooldown = COOLDOWNS[Math.min(showCount, COOLDOWNS.length - 1)]
            
            // Reset count after 4 hours of no activity (fresh start)
            if (lastClose && Date.now() - parseInt(lastClose) > 14400000) {
                localStorage.setItem("modal_show_count", "0")
            }
            
            // Check if cooldown has passed
            if (lastClose && Date.now() - parseInt(lastClose) < cooldown) {
                return
            }
            if (!isRealUser) return

            let triggers: (() => void)[] = []
            let cleanupFns: (() => void)[] = []
            let hasTriggered = false

            // 1. Aggressive Engagement Detection
            const engagementTriggers = {
                scroll: {
                    // Fire early: 20% for returning, 30% for new (catches most users)
                    threshold: conversionStatus === "returning" ? 0.2 : 0.3,
                    handler: () => {
                        const scrollHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight, document.body.offsetHeight, document.documentElement.offsetHeight, document.body.clientHeight, document.documentElement.clientHeight)
                        if ((window.scrollY + window.innerHeight) / scrollHeight >= engagementTriggers.scroll.threshold) {
                            triggerModal()
                        }
                    }
                },
                time: {
                    // Fire fast: 7s for returning, 12s for new (before bounce)
                    threshold: conversionStatus === "returning" ? 7000 : 12000,
                    timer: timeTimerRef
                },
                inactivity: {
                    // Fire on pause: 10s of no activity
                    threshold: 10000,
                    timer: inactivityTimerRef
                }
            }

            // 2. Multi-stage triggering
            const setupTriggers = () => {
                // Time-based with device detection
                timeTimerRef.current = setTimeout(() => {
                    if (!hasTriggered) triggerModal()
                }, engagementTriggers.time.threshold)

                // Scroll-based
                window.addEventListener("scroll", engagementTriggers.scroll.handler)

                // Inactivity detection
                const resetInactivityTimer = () => {
                    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
                    inactivityTimerRef.current = setTimeout(() => {
                        if (!hasTriggered) triggerModal()
                    }, engagementTriggers.inactivity.threshold)
                }

                resetInactivityTimer()
                window.addEventListener("mousemove", resetInactivityTimer)
                window.addEventListener("keypress", resetInactivityTimer)

                // Exit intent for desktop
                const handleMouseOut = (e: MouseEvent) => {
                    if (e.clientY < 0 && !hasTriggered) {
                        triggerModal()
                    }
                }

                document.addEventListener("mouseout", handleMouseOut)

                // Cleanup functions
                cleanupFns.push(
                    () => window.removeEventListener("scroll", engagementTriggers.scroll.handler),
                    () => document.removeEventListener("mouseout", handleMouseOut),
                    () => {
                        window.removeEventListener("mousemove", resetInactivityTimer)
                        window.removeEventListener("keypress", resetInactivityTimer)
                    }
                )
            }

            const triggerModal = () => {
                if (hasTriggered) return
                hasTriggered = true
                setShowModal(true)
                cleanupFns.forEach((fn) => fn())
                if (timeTimerRef.current) clearTimeout(timeTimerRef.current)
            }

            // 4. Mobile-specific adjustments (minimal delay)
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
            if (isMobile) {
                // Reduced delay: 2s to let page settle, then fire aggressively
                setTimeout(setupTriggers, 2000)
            } else {
                setupTriggers()
            }

            return () => {
                cleanupFns.forEach((fn) => fn())
                if (timeTimerRef.current) clearTimeout(timeTimerRef.current)
            }
        }
    }, [isRealUser, conversionStatus])

    // Progressive persistence - increment show count on each close
    const handleClose = () => {
        setShowModal(false)
        const currentCount = parseInt(localStorage.getItem("modal_show_count") || "0")
        localStorage.setItem("modal_show_count", (currentCount + 1).toString())
        localStorage.setItem("last_modal_close", Date.now().toString())
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
        if (timeTimerRef.current) clearTimeout(timeTimerRef.current)
    }

    // Use portal to render modal at body level while staying in React tree
    if (!isMounted || !showModal) return null

    return createPortal(
        <div className="fade-in-up-short">
            <DynamicLandingSignupModal isOpen={true} onClose={handleClose} />
        </div>,
        document.body
    )
}
